import {
    calculateTScore,
    calculateStandardScore,
    calculateScaledScore,
    calculatePercentile,
    calculateSten,
    calculateStanine,
    calculateZScore,
    tScoreToZ,
    iqToZ,
    scaledToZ,
    percentileToZ,
    stenToZ,
    stanineToZ,
} from './statistics';

// Stable Internal IDs
export type CalculatorMetricId =
    | 'raw'
    | 'mean'
    | 'sd'
    | 'z'
    | 't'
    | 'iq'
    | 'scaled10_3'
    | 'percentile'
    | 'sten'
    | 'stanine'
    | 'classification'
    | 'note';

export type CalculatorInputs = Partial<Record<CalculatorMetricId, number | string>>;
export type CalculatorOutputs = Partial<Record<CalculatorMetricId, number | string>> & {
    _source?: string;
    _error?: string;
};

// Callback to perform norm table lookup based on raw score
type NormTableLookupFn = (raw: number) => Partial<CalculatorInputs> | null;

// Callback for custom classification
type ClassificationFn = (z: number) => string;

interface CalculationConfig {
    recalculateParams?: boolean;
    rounding?: Record<string, number>;
    normTableLookup?: NormTableLookupFn; // Prioritized over Normal(μ,σ)
    classificationRules?: ClassificationFn;
}

// Helper to safely parse numbers
const parseNum = (val: string | number | undefined): number | null => {
    if (val === undefined || val === '' || val === null) return null;
    if (typeof val === 'number') return isNaN(val) ? null : val;
    const p = parseFloat(String(val).replace(',', '.'));
    return isNaN(p) ? null : p;
};

/**
 * Resolves the pivot (Z-Score) based on the hierarchy of available inputs.
 */
const resolveZ = (
    inputs: CalculatorInputs,
    config?: CalculationConfig,
): { z: number | null; source: string; missing?: string[] } => {
    const rawIn = parseNum(inputs.raw);
    const missing: string[] = [];

    // 1. Norm Table Lookup (Priority)
    if (config?.normTableLookup && rawIn !== null) {
        const lookupResult = config.normTableLookup(rawIn);
        if (lookupResult) {
            // Recursively resolve Z from the lookup result
            const lookupZ = resolveZ(lookupResult, undefined); // No recursion into lookup again
            if (lookupZ.z !== null) {
                return { z: lookupZ.z, source: `Tabela de Normas (${lookupZ.source})` };
            }
        } else {
            // Norm table was available but failed lookup
            missing.push('Norma na Tabela (lookup failed)');
        }
    }

    // 2. Explicit Z
    const zIn = parseNum(inputs.z);
    if (zIn !== null) return { z: zIn, source: 'Manual Z' };

    // 3. Inverse Lookups (Standardized Scores)
    const tIn = parseNum(inputs.t);
    if (tIn !== null) return { z: tScoreToZ(tIn), source: 'Derived from T' };

    const iqIn = parseNum(inputs.iq);
    if (iqIn !== null) return { z: iqToZ(iqIn), source: 'Derived from IQ' };

    const sIn = parseNum(inputs.scaled10_3);
    if (sIn !== null) return { z: scaledToZ(sIn), source: 'Derived from Scaled' };

    const pIn = parseNum(inputs.percentile);
    if (pIn !== null && pIn > 0 && pIn < 100)
        return { z: percentileToZ(pIn), source: 'Derived from Percentile' };

    const stenIn = parseNum(inputs.sten);
    if (stenIn !== null) return { z: stenToZ(stenIn), source: 'Derived from Sten' };

    const stanineIn = parseNum(inputs.stanine);
    if (stanineIn !== null) return { z: stanineToZ(stanineIn), source: 'Derived from Stanine' };

    // 4. Normal Distribution Fallback
    const mean = parseNum(inputs.mean);
    const sd = parseNum(inputs.sd);

    if (rawIn !== null && mean !== null && sd !== null && sd !== 0) {
        return { z: calculateZScore(rawIn, mean, sd), source: 'Normal(μ,σ)' };
    }

    // Collect missing info if we reached here
    // Strategy: Identify what "set" of data is partially present and report the gap.
    if (rawIn !== null) {
        if (mean === null) missing.push('Média (μ)');
        if (sd === null) missing.push('Desvio Padrão (σ)');
    } else {
        // If no raw, and we checked all standardized scores above...
        missing.push('Resultado Bruto (X) ou Escore Padronizado');
    }

    return { z: null, source: 'Insufficient Data', missing };
};

/**
 * Calculates classification label based on rules.
 */
const getClassification = (z: number, customRules?: ClassificationFn): string => {
    if (customRules) {
        return customRules(z);
    }
    // Default Rule (Percentile based)
    const p = calculatePercentile(z);
    if (p < 2) return 'Muito Baixo';
    if (p < 9) return 'Baixo';
    if (p < 25) return 'Média Inferior';
    if (p <= 75) return 'Médio';
    if (p <= 91) return 'Média Superior';
    if (p <= 98) return 'Alto';
    return 'Muito Alto';
};

/**
 * Main Engine Function
 */
export const calculateRow = (
    inputs: CalculatorInputs,
    config?: CalculationConfig,
): CalculatorOutputs => {
    const outputs: CalculatorOutputs = {};

    // 1. Resolve Pivot (Z)
    const { z, source, missing } = resolveZ(inputs, config);

    if (z === null) {
        const missingText = missing && missing.length > 0 ? `: Faltando ${missing.join(', ')}` : '';
        return { _error: `Dados insuficientes${missingText}` };
    }

    outputs._source = source;
    outputs.z = z; // Keep precision for internal calc

    // 2. Propagate
    outputs.t = calculateTScore(z);
    outputs.iq = calculateStandardScore(z);
    outputs.scaled10_3 = calculateScaledScore(z);
    outputs.percentile = calculatePercentile(z);
    outputs.sten = calculateSten(z);
    outputs.stanine = calculateStanine(z);

    // 3. Classification
    outputs.classification = getClassification(z, config?.classificationRules);

    // 4. Reverse Raw (optional)
    if (inputs.raw === undefined || inputs.raw === '') {
        const mean = parseNum(inputs.mean);
        const sd = parseNum(inputs.sd);
        if (mean !== null && sd !== null) {
            outputs.raw = z * sd + mean;
        }
    }

    // Format results
    const formatted: CalculatorOutputs = { ...outputs };

    // Z: 2 decimals
    if (typeof formatted.z === 'number') formatted.z = formatted.z.toFixed(2).replace('.', ',');

    // Integers
    if (typeof formatted.t === 'number') formatted.t = Math.round(formatted.t).toString();
    if (typeof formatted.iq === 'number') formatted.iq = Math.round(formatted.iq).toString();
    if (typeof formatted.scaled10_3 === 'number')
        formatted.scaled10_3 = Math.round(formatted.scaled10_3).toString();
    if (typeof formatted.percentile === 'number')
        formatted.percentile = Math.round(formatted.percentile).toString();
    if (typeof formatted.sten === 'number') formatted.sten = Math.round(formatted.sten).toString();
    if (typeof formatted.stanine === 'number')
        formatted.stanine = Math.round(formatted.stanine).toString();
    if (typeof formatted.raw === 'number')
        formatted.raw = Number.isInteger(formatted.raw)
            ? formatted.raw.toString()
            : formatted.raw.toFixed(2).replace('.', ',');

    return formatted;
};
